import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Field, LinkFieldValue, RichText, SitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import './index.scss';

import { FaRegEdit } from 'react-icons/fa';
import { LinkRenderer } from '../LinkRenderer/LinkRenderer';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import { getAPICall, postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import {
  CartItemsDetails,
  CartProductFieldsProps,
  FormatTextFieldProps,
} from '../Product/StandardProductCartDetails/StandardProductCartDetails';
import nextId from 'react-id-generator';
import { addEllipsis, getCurrencyInUSD, getDateTimePartsFromIso } from '../../utils/helper';
import { Link } from 'react-router-dom';
import { ISubscriptionInfo } from '../OptInNotification/OptInNotification';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import { UnifiedCartDataService } from '../../utils/UnifiedCartDataService';
import { ProcessOrderValidationService } from '../CheckoutPage/ProcessOrderValidationService';
import { CountryFieldProps } from '../Order/OrderSummaryCheckout/OrderSummaryCheckout';
import ToastNotification from '../ToastNotification/ToastNotification';
import { getFieldSpecificConfiguration } from '../../utils/fieldSpecificConfigHelper';
import { IFieldSpecificConfig } from '../../model/IFieldSpecificConfig';
import { MODALITY_TITLES, ProductType } from '../../utils/ProductTypes';
import { ReportType } from '../../utils/ReportTypes';
import { FaExclamationCircle } from 'react-icons/fa';
import {
  DIGITAL_DELIVERY,
  DIGITAL_PRINT_DELIVERY,
  HARD_COPY_APPLICATIONS,
  ICP_IDENTIFIER,
  LOB_DISPLAY_NAMES,
  LOB_TITLES,
  NO_DIGITAL_DELIVERY,
  PRINT_DELIVERY,
  QuantityExceedsCapacity,
  STANDARDS_DIGITAL_DELIVERY,
  STANDARDS_IDENTIFIER,
  STATISTICS_REPORT_MAX_QUANTITY,
  TRAINING_IDENTIFIER,
} from '../../utils/constants';
import { LOB_LIST, OrderDetails } from '../../context/reducer';
import { getIdpToken } from '../../utils/authHelper';
import { MdOutlinePrint } from 'react-icons/md';
import { FiDownload } from 'react-icons/fi';
import { FcDownload } from 'react-icons/fc';
import ReactToPrint from 'react-to-print';
import Tooltip from '@mui/material/Tooltip';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import DOMPurify from 'dompurify';
import { PRODUCT_TYPE_TO_PATH } from '../../model/productConfigHelper';
import { formatTermDisplayValue } from '../../utils/StatisticsStoreHelper';

export type ReviewCartOrderProps = {
  fields: {
    Link: Field<LinkFieldValue>;
    OptInText: Field<string>;
    OptInAgreementText: Field<string>;
    FormatOptions: Array<FormatOptionsFieldsProps>;
    RestrictedCountries: Array<CountryFieldProps>;
    Fields: Array<IFieldSpecificConfig>;
    PrintDisclaimer: Field<string>;
    LOBConfiguration: Array<LobConfigurationProps>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
};

export type LobConfigurationProps = {
  displayName: string;
  fields: {
    LOBIdentifier: Field<string>;
    HideSurchargeIfWaivedOff: Field<Boolean>;
    OptInNotification: Field<Boolean>;
    HasDigitalDelivery: Field<Boolean>;
    ShowProductLink: Field<Boolean>;
    HasEdition: Field<Boolean>;
    PaymentPastDeadlineMessage: Field<string>;
    GenericFailureMessage: Field<string>;
  };
};

export type FormatOptionsFieldsProps = {
  fields: FormatTextFieldProps;
  displayName: string;
  name: string;
};

export type CartOrderDetails = {
  orderId: string;
  quantity: number;
  items: CartItemsDetails[];
  lob: string;
};

const ReviewCartOrder = ({ fields }: ReviewCartOrderProps): React.JSX.Element => {
  const [cartOrders, setCartOrders] = useState<CartOrderDetails[]>([]);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [subscriptionInfo, setSubscriptionInfo] = useState(Array<ISubscriptionInfo>());
  const [isOptInLoading, setIsOptInLoading] = useState('');
  const [countryInfo, setCountryInfo] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<Field<string>>();
  const [toastStatus, setToastStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const renderRichTextMessage = (richTextTemplate?: Field<string>, message?: string) => {
    if (!richTextTemplate?.value || !message) return null;

    const replacedHtml = richTextTemplate.value.replace('##MESSAGE##', message);
    const sanitizedHtml = DOMPurify.sanitize(replacedHtml);

    return (
      <div className="notificationDetailText" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    );
  };

  const getPaymentPastDeadlineURL = (
    paymentPastDeadlineMessage?: Field<string>,
    applicationId?: string
  ) => {
    if (!paymentPastDeadlineMessage?.value || !applicationId) return null;

    const updatedHtml = paymentPastDeadlineMessage.value.replace(
      '##APPLICATION_ID##',
      `?applicationId=${applicationId}`
    );
    const sanitizedHtml = DOMPurify.sanitize(updatedHtml);
    return (
      <div className="notificationDetailText" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    );
  };

  const getSyncErrorMessageComponent = (
    item: CartItemsDetails,
    fields: any,
    lob: string
  ): JSX.Element | null => {
    let errorMessage = state?.sitecoreDictionaryData?.[item.message];

    if (errorMessage && item.isValid === false) {
      const examWindowMessage = state?.sitecoreDictionaryData?.ExamWindowExpirationMessage;
      if (item.message === examWindowMessage) {
        return getPaymentPastDeadlineURL(
          fields?.PaymentPastDeadlineMessage,
          item.product?.applicationNumber
        );
      }

      if (item.message === QuantityExceedsCapacity && item?.sessionOptions?.capacityAvailable) {
        errorMessage = errorMessage?.replace(
          '##QUANTITY##',
          item?.sessionOptions?.capacityAvailable.toString() ?? ''
        );
      }

      return renderRichTextMessage(fields?.GenericFailureMessage, errorMessage);
    } else if (item.isValid === false) {
      const genericFailureMessage =
        state?.sitecoreDictionaryData?.GenericErrorMessage.replace(
          '###LOB###',
          LOB_DISPLAY_NAMES[lob]
        ) ||
        `The item is no longer valid. Please remove from the cart and add it from ${LOB_DISPLAY_NAMES[lob]} again if needed.`;
      return renderRichTextMessage(fields?.GenericFailureMessage, genericFailureMessage);
    }

    return null;
  };

  const getFieldSpecificConfig = (productType: string) => {
    let fieldConfigData = getFieldSpecificConfiguration(productType, fields?.Fields);
    const fieldSpecificConfiguration: IFieldSpecificConfig | undefined = fieldConfigData.map(
      (config) => {
        return {
          HidePeriod: config.fields.HidePeriod,
          CartItemMaxQuantity: config.fields.CartItemMaxQuantity,
        };
      }
    )[0];
    return fieldSpecificConfiguration;
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  const showCustomToast = (message: string, toastStatus: string) => {
    setToastStatus(toastStatus);
    setToastMessage({ value: message });
    setShowToast(true);
  };

  function updateSubscriptionInfoApiCall(optIn: boolean, productDetail: CartProductFieldsProps) {
    if (optIn) {
      const sendSubscriptionInfoRequest = {
        productId: productDetail?.productId,
        PublicationType: productDetail?.publicationType,
        PublicationNumber: productDetail?.publicationNbr,
      };
      postAPICall(
        `${process.env.REACT_APP_CATALOG_URL}/subscription/useroptin`,
        sendSubscriptionInfoRequest
      )
        .then(function (response: any) {
          if (response && response.status === 200) {
            fetchSubscriptionData();
            showCustomToast(
              state?.sitecoreDictionaryData &&
                state?.sitecoreDictionaryData.OptInConfirmationMessage
                ? state?.sitecoreDictionaryData.OptInConfirmationMessage
                : 'You have opted in for notifications for this product.',
              'success'
            );
          }
        })
        .catch((error) => {
          if (
            error &&
            error.response.status === 400 &&
            error.response.data === 'USERUNSUBSCRIBED'
          ) {
            showCustomToast(
              state?.sitecoreDictionaryData &&
                state?.sitecoreDictionaryData.OptInUnsubscribedErrorMsg
                ? state?.sitecoreDictionaryData.OptInUnsubscribedErrorMsg
                : 'Failed to opt-in since you are unsubscribed user. Please contact Customer support to subscribe.',
              'error'
            );
            return;
          }
          showCustomToast(
            state?.sitecoreDictionaryData && state?.sitecoreDictionaryData.OptInErrorMessage
              ? state?.sitecoreDictionaryData.OptInErrorMessage
              : 'Your choice to opt-in has not been processed. Please try again after some time.',
            'error'
          );
        })
        .finally(() => {
          setIsOptInLoading('');
        });
    } else {
      postAPICall(
        `${process.env.REACT_APP_CATALOG_URL}/Subscription/useroptout/${productDetail?.productId}`,
        {}
      )
        .then(function (response: any) {
          if (response && response.status === 200) {
            fetchSubscriptionData();
          }
        })
        .finally(() => {
          setIsOptInLoading('');
        });
    }
  }

  function getSubscriptionInfoApiCall() {
    getAPICall(`${process.env.REACT_APP_CATALOG_URL}/subscription`).then(function (response: any) {
      if (response && response.data) {
        setSubscriptionInfo(response.data);
      } else {
        setSubscriptionInfo([]);
      }
    });
  }

  function productOptedIn(product: CartProductFieldsProps) {
    let optedIn = false;
    const count =
      subscriptionInfo && subscriptionInfo.filter((x) => x.productId === product?.productId);
    if (count.length > 0) {
      optedIn = true;
    }
    return optedIn;
  }

  useEffect(() => {
    getSubscriptionInfoApiCall();
  }, []);

  const fetchSubscriptionData = () => {
    getSubscriptionInfoApiCall();
  };

  function onChange(e: any, item: CartItemsDetails, product: CartProductFieldsProps) {
    setIsOptInLoading(item.lineItemId);
    if (e.target.checked) {
      updateSubscriptionInfoApiCall(true, product);
    } else {
      updateSubscriptionInfoApiCall(false, product);
    }
  }

  let isPDFExists = false,
    isPrintExists = false,
    isPrintAndPDFExists = false,
    isDatasheetExists = false,
    isStatisticsReportAvailable = false;

  function checkoutUpdate(cartItems: Array<CartItemsDetails>) {
    // get the delivery status
    isPDFExists =
      isPDFExists ||
      cartItems.some(
        (x) => x.priceOption?.format === fields?.FormatOptions[0]?.fields?.FormatType?.value
      );

    isPrintExists =
      isPrintExists ||
      cartItems.some(
        (x) => x.priceOption?.format === fields?.FormatOptions[1]?.fields?.FormatType?.value
      );

    isPrintAndPDFExists =
      isPrintAndPDFExists ||
      cartItems.some(
        (x) => x.priceOption?.format === fields?.FormatOptions[2]?.fields?.FormatType?.value
      );

    isDatasheetExists = isDatasheetExists || cartItems.some((x) => x.product?.isDatasheet === true);

    const cartLobs = Object.keys(state?.cartCounts?.lobCounts || {}).filter(
      (lob) => (state?.cartCounts?.lobCounts?.[lob] ?? 0) > 0
    );

    const hideDigitalDelivery = cartLobs.some((lob) => {
      const lobConfig = getLobConfig(lob);
      return lobConfig?.fields?.HasDigitalDelivery?.value === false;
    });

    let deliveryStatus: string = '';
    if (isPrintAndPDFExists || (isPrintExists && (isPDFExists || isDatasheetExists))) {
      deliveryStatus = DIGITAL_PRINT_DELIVERY;
    } else if (isPrintExists) {
      deliveryStatus = PRINT_DELIVERY;
    } else if (isPDFExists || isDatasheetExists) {
      deliveryStatus = DIGITAL_DELIVERY;
    } else {
      deliveryStatus = NO_DIGITAL_DELIVERY;
    }

    if (deliveryStatus !== NO_DIGITAL_DELIVERY && hideDigitalDelivery) {
      deliveryStatus = `${STANDARDS_DIGITAL_DELIVERY} ${deliveryStatus}`;
    }
    // Check whether statistics report available
    isStatisticsReportAvailable =
      isStatisticsReportAvailable || cartItems.some((item) => item.product?.isStatisticsReport);

    // Dispatch the values
    if (dispatch) {
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          hasStatisticsReport: isStatisticsReportAvailable,
          deliveryStatus: deliveryStatus,
        },
      });
    }
  }

  function statisticsReportQuantityLimitCheck(cartItems: Array<CartItemsDetails>) {
    const fieldConfig = getFieldSpecificConfig(ProductType.StatisticsReport);
    const isStatisticsReportQuantityExceeded = cartItems.some(
      (item) =>
        item?.product?.isStatisticsReport === true &&
        (item?.priceOption?.quantity ?? 0) >
          (fieldConfig?.CartItemMaxQuantity?.value !== undefined
            ? fieldConfig?.CartItemMaxQuantity?.value
            : STATISTICS_REPORT_MAX_QUANTITY)
    );
    isStatisticsReportQuantityExceeded === true &&
      showCustomToast(
        state?.sitecoreDictionaryData &&
          state?.sitecoreDictionaryData.StatisticsReportQuantityExceedMessage
          ? state?.sitecoreDictionaryData.StatisticsReportQuantityExceedMessage
          : 'Your cart contains more than one quantity of the same statistics report. Please review and update your cart.',
        'warning'
      );
  }

  const fetchData = async () => {
    const cartOrders: CartOrderDetails[] = [];
    const lobOrderDetails: { [key: string]: OrderDetails } = {};
    const idpToken = getIdpToken();
    let syncFailureLOBs: string[] = [];
    let hardCopyQuantity = 0;

    setIsLoading(true);
    for (const lob of LOB_LIST) {
      try {
        const response = await getAPICall(
          `${process.env.REACT_APP_CART_URL}/cartOrder/checkout?lob=${lob}`,
          {
            headers: {
              'B2C-Authorization': idpToken,
            },
          }
        );

        if (response && response.data) {
          cartOrders.push(response.data);

          const orderIds: string[] = [];
          let hasSyncFailure = false;
          (response.data.items || []).forEach((item: any) => {
            if (item.orderId && !orderIds.includes(item.orderId)) {
              orderIds.push(item.orderId);
            }

            if (item.isValid === false) {
              hasSyncFailure = true;
            }

            if (item.product && HARD_COPY_APPLICATIONS.includes(item.product.productId)) {
              hardCopyQuantity += item.priceOption?.quantity;
            }
          });

          if (hasSyncFailure) {
            syncFailureLOBs.push(LOB_DISPLAY_NAMES[lob]);
          }

          if (orderIds && orderIds.length > 0) {
            const existing = state?.orderDetails?.[lob];
            lobOrderDetails[lob] = {
              orderIds,
              paymentType: existing?.paymentType ?? '',
            };
          }
          UnifiedCartDataService.setCartInfo(lob, response.data.items, false);

          checkoutUpdate(response.data.items);
          statisticsReportQuantityLimitCheck(response.data.items);
        }
      } catch (err) {
        console.error(`Error fetching cart order for ${lob}:`, err);
      }
    }

    if (dispatch) {
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          orderDetails: Object.fromEntries(
            LOB_LIST.map((lob) => [lob, lobOrderDetails[lob] || { orderIds: [], paymentType: '' }])
          ),
          syncFailedCheckoutLOBs: syncFailureLOBs,
          hardCopyQuantity,
        },
      });
    }

    setCartOrders(cartOrders);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [state?.togglePaymentResponse]);

  useEffect(() => {
    getShippingCountry();
  }, [state?.toggleCalculateOrder]);

  function getShippingCountry() {
    let shippingCountry: string = '';
    getAPICall(`${process.env.REACT_APP_CART_URL}/checkout/getshippingcountry`)
      .then(function (response: any) {
        if (response && response.data) {
          shippingCountry = response.data;
        }
      })
      .finally(() => {
        if (!shippingCountry && shippingCountry.length === 0) {
          shippingCountry = ProcessOrderValidationService.getShippingInfo()?.shippingInfo?.country;
        }
        setCountryInfo(shippingCountry);
      });
  }

  function getProductUrl(productDetails: CartProductFieldsProps, lob: string): string {
    const productType = productDetails?.modality || productDetails?.productType;
    const path = PRODUCT_TYPE_TO_PATH[productType];
    let queryString = '';

    if (productType === ProductType.StatisticsReport) {
      queryString = `?reportYear=${productDetails?.year}&term=${productDetails?.term}`;
    } else if (!productDetails?.isRecent && getLobConfig(lob)?.fields?.HasEdition?.value) {
      queryString = `?edition=${productDetails?.edition}`;
    }

    return `/${path}/${productDetails?.productId}${queryString}`;
  }

  function seeMore(emailAddresses: Array<string>, userEmail: any, id: number, quantity: number) {
    let emailAddressList: Array<string> = [];
    var count: any = {};

    if (quantity !== emailAddresses.length) {
      for (let i = 0; i < quantity - emailAddresses.length; i++) {
        emailAddresses.push(userEmail.toLowerCase());
      }
    }
    emailAddresses.forEach(function (i) {
      count[i.toLowerCase()] = (count[i.toLowerCase()] || 0) + 1;
    });
    for (let [key, value] of Object.entries(count)) {
      emailAddressList.push(`${key} (${value})`);
    }
    return (
      <div className="productDetailInnerTitleText">
        <input type="checkbox" className="read-more-state" id={`post-${id}`} />
        <div className="productDetailEmails read-more-wrap">
          {emailAddressList.slice(0, 2).join(',')}
          {emailAddressList.length > 2 && (
            <>
              <span className="read-more-target">,{emailAddressList.slice(2).join(',')}</span>
              <label htmlFor={`post-${id}`} className="read-more-trigger"></label>
            </>
          )}
        </div>
      </div>
    );
  }

  function getFormat(format: string) {
    let shouldHighlight = false;
    if (fields?.FormatOptions !== undefined) {
      let formatTypes = fields.FormatOptions.filter(
        (x: any) => x.fields.FormatType?.value === format
      );
      if (formatTypes && formatTypes.length > 0) {
        if (
          fields.RestrictedCountries?.some((x) => x.fields.CountryCode?.value === countryInfo) &&
          formatTypes.some((x) => x.name === 'PrintEdition' || x.name === 'PrintEdition_And_PDF')
        ) {
          shouldHighlight = true;
        }
      }
    }
    return (
      <>
        <span className={`${shouldHighlight ? 'highlight' : ''}`}>{format}</span>
      </>
    );
  }

  const getLobConfig = (lob: string) =>
    fields?.LOBConfiguration?.find((config) => config.fields?.LOBIdentifier?.value === lob);

  const lobParam = useMemo(() => {
    const cartLOBs = state?.cartCounts?.lobCounts;

    for (const lob in cartLOBs) {
      if (cartLOBs[lob] > 0) return lob;
    }
    return null;
  }, [state?.cartCounts?.lobCounts]);

  const renderFieldSection = (label: string, value: React.ReactNode) => (
    <div className="fieldSection">
      <p>{label}:</p>
      <p className="productDetailInnerTitleText">{value}</p>
    </div>
  );

  const reviewOrderRef = useRef<HTMLDivElement>(null);

  const handleDownloadReviewOrder = () => {
    if (typeof window !== 'undefined') {
      const html2pdf = require('html2pdf.js');
      setIsDownloading(true);
      const content = reviewOrderRef.current;
      if (content) {
        const options = {
          filename: 'ReviewOrder.pdf',
          image: { type: 'jpeg' },
          jsPDF: {
            unit: 'pt',
            format: 'a4',
            orientation: 'portrait',
          },
          margin: 10,
        };

        html2pdf()
          .set(options)
          .from(content)
          .save()
          .then(() => setIsDownloading(false));
      }
    }
  };

  const getReviewCartDetailsSection = (lob: string, item: CartItemsDetails, id: number) => {
    switch (lob) {
      case STANDARDS_IDENTIFIER:
        return standardsCartDetails(item, id);
      case ICP_IDENTIFIER:
        return icpCartDetails(item, id, lob);
      case TRAINING_IDENTIFIER:
        return trainingCartDetails(item, lob);
      default:
        return null;
    }
  };

  function getReviewCartOrderSkeleton() {
    return (
      <div className="orderContainer">
        <div className="lobHeading">
          <p>
            <LoadingSkeleton theme="dark" />
          </p>
        </div>

        <div className="reviewItemOne">
          <div className="reviewContainer">
            <div className="reviewOrderDetailsSectionMain">
              <div className="reviewOrderDetailsLeft">
                <div className="reviewOrderDetailsTop">
                  <a className="productTitle" href="#">
                    <LoadingSkeleton theme="light" />
                  </a>
                </div>

                <div className="reviewOrderDetailsBottom">
                  <div className="fieldSection">
                    <p>Format:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>

                  <div className="fieldSection">
                    <p>Quantity:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>

                  <div className="fieldSection">
                    <p>Language:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>

                  <div className="fieldSection">
                    <p>Send to:</p>
                    <p className="productDetailInnerTitleText">
                      <LoadingSkeleton theme="light" />
                    </p>
                  </div>
                </div>
              </div>

              <div className="reviewOrderDetailsRight">
                <div className="productPrice">
                  <LoadingSkeleton theme="light" />
                </div>
              </div>
            </div>

            <div className="standardOptInDiv">
              <div className="optInNotifications">
                <label className="optinnotificationtext">
                  <div>
                    <LoadingSkeleton theme="light" />
                  </div>
                </label>
                <div className="tooltip">
                  <div className="tooltipArea">
                    <button
                      className="MuiButtonBase-root MuiButton-root MuiButton-text"
                      type="button"
                    >
                      <span className="MuiButton-label">
                        <LoadingSkeleton theme="light" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="reviewItemDivider"></div>
          </div>
        </div>
      </div>
    );
  }

  const standardsCartDetails = (item: CartItemsDetails, id: number) => {
    const fieldSpecificConfig = getFieldSpecificConfig(item.product?.productType);

    const showWarning =
      item.product?.isStatisticsReport &&
      fieldSpecificConfig &&
      item.priceOption?.quantity >
        (fieldSpecificConfig.CartItemMaxQuantity?.value ?? STATISTICS_REPORT_MAX_QUANTITY);

    return (
      <div className="reviewOrderDetailsBottom">
        {renderFieldSection('Format', getFormat(item.priceOption?.format))}

        <div className="fieldSection">
          <div className="quantity-header-div">
            <p>Quantity:</p>
            {showWarning && (
              <FaExclamationCircle className="quantity-error" title="Quantity limit exceeded." />
            )}
          </div>
          <p className="productDetailInnerTitleText">{item.priceOption?.quantity}</p>
        </div>

        {renderFieldSection('Language', item.priceOption?.language)}

        {!fieldSpecificConfig?.HidePeriod?.value &&
          renderFieldSection(
            'Period',
            item?.product?.term === ReportType.Annual
              ? item?.product.year
              : `${formatTermDisplayValue(item?.product.term)}, ${item?.product.year}`
          )}

        {item?.priceOption?.format !== 'Print Edition' &&
          renderFieldSection(
            'Send to',
            seeMore(item.sendToEmailAddresses, state?.userEmail, id, item.priceOption?.quantity)
          )}
      </div>
    );
  };

  const icpCartDetails = (item: CartItemsDetails, id: number, lob: string) => {
    return (
      <div className="reviewOrderDetailsBottom">
        {item.product?.examWindow && renderFieldSection('Exam Window', item.product.examWindow)}
        {item.priceOption?.language && renderFieldSection('Language', item.priceOption?.language)}
        {renderFieldSection('Invoice Number', item.product?.apiPoNumber)}
        {getSyncErrorMessageComponent(item, getLobConfig(lob)?.fields, lob)}
      </div>
    );
  };

  const trainingCartDetails = (item: CartItemsDetails, lob: string) => {
    const startDate = item.sessionOptions?.startDate
      ? `${getDateTimePartsFromIso(item.sessionOptions.startDate)?.date} ${
          item.sessionOptions?.timezone
        }`
      : '';

    const endDate = item.sessionOptions?.endDate
      ? `${getDateTimePartsFromIso(item.sessionOptions.endDate)?.date} ${
          item.sessionOptions?.timezone
        }`
      : '';

    return (
      <div className="reviewOrderDetailsBottom">
        <div className="reviewOrderDetailsRow">
          {item.product?.modality &&
            renderFieldSection('Modality', MODALITY_TITLES[item.product.modality])}
          {item.product?.language && renderFieldSection('Language', item.product.language)}
          {item.priceOption?.quantity && renderFieldSection('Quantity', item.priceOption?.quantity)}
        </div>

        {startDate && endDate && (
          <div className="reviewOrderDetailsRow">
            {startDate && renderFieldSection('Starts', startDate)}
            {endDate && renderFieldSection('Ends', endDate)}
          </div>
        )}

        {item.sessionOptions?.description && (
          <div className="reviewOrderDetailsRow">
            <div className="fieldSection">
              <p className="productDescription">Description:</p>
              <div
                className="productDetailInnerTitleText"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item.sessionOptions.description || ''),
                }}
              />
            </div>
          </div>
        )}
        {getSyncErrorMessageComponent(item, getLobConfig(lob)?.fields, lob)}
      </div>
    );
  };

  const getReviewContainer = () => {
    return (
      <div className="reviewOrderContainer" ref={reviewOrderRef}>
        {isLoading ? (
          getReviewCartOrderSkeleton()
        ) : (
          <>
            <div className="reviewCartOrder">
              {cartOrders
                ?.filter((order) => (state?.cartCounts?.lobCounts[order.lob] ?? 0) > 0)
                .map((order) => (
                  <div key={nextId()} className="orderContainer">
                    <div className="lobHeading">
                      <p>{LOB_TITLES[order.lob] ?? order.lob}</p>
                    </div>
                    <div className="reviewItemOne">
                      {order.items &&
                        order.items.length > 0 &&
                        order.items.map((item: CartItemsDetails, id: number) => (
                          <React.Fragment key={id}>
                            <div className="reviewContainer">
                              <div className="reviewOrderDetailsSectionMain" key={nextId()}>
                                <div className="reviewOrderDetailsLeft">
                                  <div className="reviewOrderDetailsTop">
                                    {item.product?.title && (
                                      <div className="productTitleWrapper">
                                        {getLobConfig(order?.lob)?.fields?.ShowProductLink
                                          ?.value === true ? (
                                          <Link
                                            to={getProductUrl(item.product, order.lob)}
                                            id={`${item.product?.productId}${item.product?.edition}`}
                                            className="productTitle"
                                          >
                                            {item.product.title}
                                            {addEllipsis(item.product.title)}
                                          </Link>
                                        ) : (
                                          <div className="productTitle hide-link">
                                            {item.product.title}
                                            {addEllipsis(item.product.title)}
                                          </div>
                                        )}

                                        {item.isValid === false &&
                                          item.message ===
                                            state?.sitecoreDictionaryData
                                              ?.SyncOrderErrorMessage && (
                                            <Tooltip
                                              title={
                                                state?.sitecoreDictionaryData
                                                  ?.SyncOrderFailureMessage ||
                                                'It looks like the ICP order may have changed. Please remove and re-add the items to proceed.'
                                              }
                                            >
                                              <SyncProblemIcon className="syncIcon" />
                                            </Tooltip>
                                          )}
                                      </div>
                                    )}
                                  </div>

                                  {getReviewCartDetailsSection(order.lob, item, id)}
                                </div>

                                <div className="reviewOrderDetailsRight">
                                  {item.priceOption && (
                                    <div className="productPrice">
                                      {getCurrencyInUSD(
                                        Number(item.priceOption?.quantity) *
                                          Number(item.priceOption?.unitPrice)
                                      )}
                                    </div>
                                  )}

                                  {item.priceOption &&
                                    item.product?.isSurchargeApplied &&
                                    item.product?.surchargeCost > 0 &&
                                    (() => {
                                      const lobConfig = getLobConfig(order.lob);
                                      return (
                                        <div className="productPrice">
                                          {item.product?.isSurchargeWaivedOff &&
                                          lobConfig?.fields?.HideSurchargeIfWaivedOff?.value
                                            ? null
                                            : item.product?.surchargeType && (
                                                <>
                                                  {item.product?.surchargeType}:
                                                  <span
                                                    className={`surcharge ${
                                                      item.product?.isSurchargeWaivedOff
                                                        ? 'strikedThrough'
                                                        : ''
                                                    }`}
                                                  >
                                                    {getCurrencyInUSD(item.product?.surchargeCost)}
                                                  </span>
                                                </>
                                              )}
                                        </div>
                                      );
                                    })()}
                                </div>
                              </div>

                              {getLobConfig(order?.lob)?.fields?.OptInNotification?.value &&
                                ((item.product?.productType ?? '') !==
                                  ProductType.StatisticsReport ||
                                  item.product?.reportType !== ReportType.Quarterly) && (
                                  <div className="standardOptInDiv">
                                    <div className="optInNotifications">
                                      <label className="optinnotificationtext">
                                        {isOptInLoading === item.lineItemId ? (
                                          <LoadingSkeleton
                                            theme="light"
                                            minWidth="22px"
                                            loaderHeight="20px"
                                            loaderWidth="22px"
                                          />
                                        ) : (
                                          <>
                                            <input
                                              type="checkbox"
                                              className="checkbox"
                                              defaultChecked={productOptedIn(item.product)}
                                              onChange={(e) => onChange(e, item, item.product)}
                                              id={item.lineItemId}
                                              key={nextId()}
                                            ></input>
                                          </>
                                        )}

                                        <RichText field={fields?.OptInAgreementText} />
                                      </label>
                                      <CustomTooltip
                                        fields={{
                                          'Tooltip Text': fields?.OptInText,
                                        }}
                                      />
                                      <br></br>
                                    </div>
                                  </div>
                                )}

                              <div className="reviewItemDivider" />
                            </div>
                          </React.Fragment>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
            <div className="print-disclaimer">
              <RichText field={fields?.PrintDisclaimer} />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="reviewCartOrder">
      <hr />
      <ToastNotification
        fields={{
          ToastMessage: toastMessage,
          ButtonText: undefined,
        }}
        showToast={showToast}
        removeToast={toastRemove}
        toastStatus={toastStatus}
      />
      <div className="reviewOrderEdit"></div>

      <div className="reviewItemOne">
        <div className="reviewOrderSection">
          <div className="reviewOrderSNo">3</div>
          <div className="reviewOrderTitle">
            <p>Review Order</p>
          </div>
          <div className="reviewOrderEdit">
            <ReactToPrint
              trigger={() => (
                <button name="print" className="download-button">
                  <MdOutlinePrint /> Print
                </button>
              )}
              content={() => reviewOrderRef.current}
            />
            <button
              className="download-button"
              disabled={isDownloading}
              onClick={handleDownloadReviewOrder}
            >
              {isDownloading ? <FcDownload></FcDownload> : <FiDownload></FiDownload>} Download
            </button>
            <LinkRenderer
              fields={{
                Link: {
                  value: {
                    ...fields.Link.value,
                    querystring: lobParam ? `lob=${lobParam}` : '',
                  },
                },
              }}
              prefixIcon={<FaRegEdit />}
            />
          </div>
        </div>
      </div>

      {getReviewContainer()}

      <hr />
    </div>
  );
};

export default ReviewCartOrder;
